import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiBook, FiAward, FiGraduationCap, FiFileText, FiBriefcase, FiShield, FiGlobe, FiUsers, FiHome, FiMonitor } = FiIcons;

const ServiceCategories = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Static fallback data
  const staticServiceCategories = [
    {
      id: 'student',
      title: 'Student Services',
      description: 'Educational and academic application services',
      services: [
        {
          id: 'jee-mains',
          name: 'JEE Mains',
          subtitle: 'Joint Entrance Examination',
          icon: FiBook,
          color: 'from-blue-500 to-blue-600',
          description: 'Engineering entrance examination applications'
        },
        {
          id: 'neet',
          name: 'NEET',
          subtitle: 'National Eligibility cum Entrance Test',
          icon: FiAward,
          color: 'from-green-500 to-green-600',
          description: 'Medical entrance examination applications'
        },
        {
          id: 'vit',
          name: 'VIT',
          subtitle: 'Vellore Institute of Technology',
          icon: FiGraduationCap,
          color: 'from-purple-500 to-purple-600',
          description: 'VIT university applications'
        },
        {
          id: 'amrita',
          name: 'AMRITA',
          subtitle: 'VISHWA VIDYAPEETHAM',
          icon: FiBook,
          color: 'from-indigo-500 to-indigo-600',
          description: 'Amrita university applications'
        },
        {
          id: 'srm',
          name: 'SRM',
          subtitle: 'INSTITUTE OF SCIENCE & TECHNOLOGY',
          icon: FiGraduationCap,
          color: 'from-red-500 to-red-600',
          description: 'SRM university applications'
        },
        {
          id: 'bitsat',
          name: 'BITSAT',
          subtitle: 'Birla Institute of Technology and Science Admission Test',
          icon: FiBook,
          color: 'from-yellow-500 to-yellow-600',
          description: 'BITS admission test applications'
        },
        {
          id: 'iiit-hyderabad',
          name: 'IIIT HYDERABAD',
          subtitle: 'The International Institute of Information Technology',
          icon: FiGraduationCap,
          color: 'from-teal-500 to-teal-600',
          description: 'IIIT Hyderabad applications'
        },
        {
          id: 'tg-eapcet',
          name: 'TG EAPCET',
          subtitle: 'Telangana Engineering, Agriculture & Pharmacy Common Entrance Test',
          icon: FiBook,
          color: 'from-orange-500 to-orange-600',
          description: 'Telangana EAPCET applications'
        },
        {
          id: 'ap-eapcet',
          name: 'AP EAPCET',
          subtitle: 'Andhra Pradesh Engineering, Agriculture and Pharmacy Common Entrance Test',
          icon: FiGraduationCap,
          color: 'from-pink-500 to-pink-600',
          description: 'Andhra Pradesh EAPCET applications'
        },
        {
          id: 'other-universities',
          name: 'Other Universities',
          subtitle: 'Various educational institutions',
          icon: FiBook,
          color: 'from-gray-500 to-gray-600',
          description: 'Other university applications'
        }
      ]
    },
    {
      id: 'job-seeker',
      title: 'Job Seeker Services',
      description: 'Professional job application and career services',
      services: [
        {
          id: 'job-applications',
          name: 'Job Applications',
          subtitle: 'Professional job application assistance',
          icon: FiBriefcase,
          color: 'from-green-500 to-green-600',
          description: 'Help with job application forms and processes'
        },
        {
          id: 'government-job-forms',
          name: 'Government Job Forms',
          subtitle: 'Government employment applications',
          icon: FiFileText,
          color: 'from-blue-500 to-blue-600',
          description: 'Government job application assistance'
        },
        {
          id: 'resume-building',
          name: 'Resume Building',
          subtitle: 'Professional resume creation',
          icon: FiUsers,
          color: 'from-purple-500 to-purple-600',
          description: 'Professional resume and CV building services'
        },
        {
          id: 'career-guidance',
          name: 'Career Guidance',
          subtitle: 'Career counseling and guidance',
          icon: FiGlobe,
          color: 'from-indigo-500 to-indigo-600',
          description: 'Career counseling and professional guidance'
        }
      ]
    },
    {
      id: 'other',
      title: 'Other Services',
      description: 'Additional application and verification services',
      services: [
        {
          id: 'document-verification',
          name: 'Document Verification',
          subtitle: 'Official document verification',
          icon: FiShield,
          color: 'from-green-500 to-green-600',
          description: 'Official document verification services'
        },
        {
          id: 'passport-applications',
          name: 'Passport Applications',
          subtitle: 'Passport application assistance',
          icon: FiGlobe,
          color: 'from-blue-500 to-blue-600',
          description: 'Passport application and renewal services'
        },
        {
          id: 'visa-applications',
          name: 'Visa Applications',
          subtitle: 'Visa application assistance',
          icon: FiFileText,
          color: 'from-purple-500 to-purple-600',
          description: 'Visa application and documentation services'
        },
        {
          id: 'other-government-forms',
          name: 'Other Government Forms',
          subtitle: 'Various government applications',
          icon: FiUsers,
          color: 'from-orange-500 to-orange-600',
          description: 'Other government form applications'
        }
      ]
    }
  ];

  // Fetch services from database
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('services_dy3xr')
          .select('*');

        if (error) throw error;

        if (data && data.length > 0) {
          // Remove duplicates by name
          const uniqueServices = data.filter((service, index, self) => 
            index === self.findIndex(s => s.name === service.name)
          );

          // Determine enabled flag per row
          const isRowEnabled = (row) => {
            const dp = row?.detailed_pricing || {};
            if (typeof dp.enabled === 'boolean') return dp.enabled;
            if (typeof row?.enabled === 'boolean') return row.enabled;
            if (typeof row?.is_enabled === 'boolean') return row.is_enabled;
            if (typeof row?.active === 'boolean') return row.active;
            return true; // default enabled
          };

          // Filter out disabled services
          const enabledServices = uniqueServices.filter(isRowEnabled);
          
          // Transform database services into category structure
          const transformedCategories = transformServicesToCategories(enabledServices);
          setServiceCategories(transformedCategories);
          console.log('Loaded dynamic services:', enabledServices.length, '(removed', data.length - enabledServices.length, 'disabled or duplicates)');
        } else {
          // Use static fallback if no services in database
          setServiceCategories(staticServiceCategories);
          console.log('Using static fallback services');
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        toast.error('Failed to load services, using defaults');
        setServiceCategories(staticServiceCategories);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();

    // Set up realtime subscription for service updates
    const channel = supabase
      .channel('services-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'services_dy3xr'
        },
        (payload) => {
          console.log('Service updated:', payload);
          fetchServices();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Color palette for services
  const colorPalette = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-red-500 to-red-600',
    'from-indigo-500 to-indigo-600',
    'from-yellow-500 to-yellow-600',
    'from-pink-500 to-pink-600',
    'from-teal-500 to-teal-600',
    'from-orange-500 to-orange-600',
    'from-cyan-500 to-cyan-600',
    'from-lime-500 to-lime-600',
    'from-amber-500 to-amber-600',
    'from-emerald-500 to-emerald-600',
    'from-violet-500 to-violet-600',
    'from-fuchsia-500 to-fuchsia-600',
    'from-rose-500 to-rose-600'
  ];

  // Category configurations with distinct styling
  const categoryConfig = {
    application: {
      id: 'application',
      title: 'Educational Applications',
      description: 'College, university and entrance exam applications',
      icon: FiGraduationCap,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      titleColor: 'text-blue-900',
      descColor: 'text-blue-700',
      badgeColor: 'bg-blue-100 text-blue-800',
      services: []
    },
    'job-services': {
      id: 'job-services',
      title: 'Job Services',
      description: 'Career guidance, resume building and job applications',
      icon: FiBriefcase,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      titleColor: 'text-green-900',
      descColor: 'text-green-700',
      badgeColor: 'bg-green-100 text-green-800',
      services: []
    },
    other: {
      id: 'other',
      title: 'Other Services',
      description: 'Document verification, passport, visa and government forms',
      icon: FiShield,
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      titleColor: 'text-purple-900',
      descColor: 'text-purple-700',
      badgeColor: 'bg-purple-100 text-purple-800',
      services: []
    }
  };

  // Transform database services into category structure
  const transformServicesToCategories = (services) => {
    const categories = JSON.parse(JSON.stringify(categoryConfig));

    // Define the desired order for services
    const serviceOrder = [
      // Student Services (Educational Applications)
      'JEE Mains', 'NEET', 'VIT', 'AMRITA', 'SRM', 'BITSAT', 'IIIT HYDERABAD', 'TG EAPCET', 'AP EAPCET', 'Other Universities',
      // Job Seeker Services
      'Job Applications', 'Government Job Forms', 'Resume Building', 'Career Guidance',
      // Other Services
      'Document Verification', 'Passport Applications', 'Visa Applications', 'Other Government Forms'
    ];

    let colorIndex = 0;

    services.forEach(service => {
      let categoryId = service.category || 'other';
      
      // Map job-related categories to 'job-services'
      if (categoryId === 'job-seeker' || categoryId === 'job' || 
          service.name.toLowerCase().includes('job') || 
          service.name.toLowerCase().includes('resume') ||
          service.name.toLowerCase().includes('career')) {
        categoryId = 'job-services';
      }
      
      // Map student/education categories to 'application'
      if (categoryId === 'student' || categoryId === 'education') {
        categoryId = 'application';
      }
      
      const serviceId = generateServiceId(service.name);
      
      // Assign color from palette or use service's color if specified
      const serviceColor = service.color || colorPalette[colorIndex % colorPalette.length];
      colorIndex++;
      
      const transformedService = {
        id: serviceId,
        name: service.name,
        icon: FiIcons[service.icon] || FiIcons.FiFileText,
        color: serviceColor,
        categoryId: categoryId
      };

      if (categories[categoryId]) {
        categories[categoryId].services.push(transformedService);
      } else {
        categories.other.services.push(transformedService);
      }
    });

    // Sort services within each category according to the desired order
    Object.keys(categories).forEach(categoryKey => {
      categories[categoryKey].services.sort((a, b) => {
        const indexA = serviceOrder.indexOf(a.name);
        const indexB = serviceOrder.indexOf(b.name);

        // If both services are in the desired order list, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one service is in the list, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in the list, maintain original order (by insertion)
        return 0;
      });
    });

    return Object.values(categories).filter(cat => cat.services.length > 0);
  };

  // Generate service ID from name (for URL routing)
  const generateServiceId = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    // Navigate to service details page with service ID
    navigate(`/service-details/${service.id}`);
  };

  const handleBack = () => {
    navigate('/service-type-selection');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Service Categories
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our comprehensive range of application services
          </p>
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex items-center"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="w-4 h-4 mr-2" />
            Back to Service Type Selection
          </Button>
        </motion.div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Service Categories */}
            <div className="space-y-12">
              {serviceCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className={`p-6 rounded-xl border-2 ${category.bgColor} ${category.borderColor}`}
            >
              {/* Category Header with Icon and Badge */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <div className={`p-3 rounded-full ${category.badgeColor} mr-3`}>
                    <SafeIcon icon={category.icon} className="w-8 h-8" />
                  </div>
                  <div className="text-left">
                    <h2 className={`text-2xl font-bold ${category.titleColor} mb-1`}>
                      {category.title}
                    </h2>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${category.badgeColor}`}>
                      {category.services.length} {category.services.length === 1 ? 'Service' : 'Services'}
                    </span>
                  </div>
                </div>
                <p className={`${category.descColor} font-medium`}>{category.description}</p>
              </div>

              {/* Service Cards Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {category.services.map((service, serviceIndex) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: (categoryIndex * 0.1) + (serviceIndex * 0.05) }}
                  >
                    <Card
                      className="p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105"
                      onClick={() => handleServiceClick(service)}
                    >
                      {/* Service Icon */}
                      <div className={`bg-gradient-to-r ${service.color} text-white p-3 rounded-lg mb-3 flex justify-center`}>
                        <SafeIcon icon={service.icon} className="w-6 h-6" />
                      </div>

                      {/* Service Name */}
                      <h3 className="font-semibold text-gray-900 text-center mb-1">
                        {service.name}
                      </h3>

                      {/* Service Subtitle */}
                      {/* Service Name Only - No duplicate description */}
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
            </div>

            {/* Information Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12"
            >
              <Card className="p-6 bg-blue-50 border-blue-200">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiIcons.FiInfo} className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>• Click on any service to view detailed pricing and options</li>
                      <li>• Choose between Online and Home service types</li>
                      <li>• Select your category (OC, EWS/OBC, SC/ST/PWD)</li>
                      <li>• Complete payment and application process</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCategories;
